import { useEffect, useMemo, useState } from 'react';
import { Button, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { SearchFilterInput } from '../SearchFilterInput';
import { EntityDirectorySelectFilter } from '../shared/entityDirectory/EntityDirectorySelectFilter';

export interface SelectedFilterValues {
  branchId: number | null;
  articleNumber: string;
  shipmentType: string | null;
  trackingNumber: string;
  customerId: number | null;
  includeDispatched: boolean;
  paOrItemCode: string;
}

const shipmentTypes = [
  { label: 'Internal', value: 'internal' },
  { label: 'External', value: 'external' },
];

export function useFilters({
  defaultValues,
}: {
  defaultValues: SelectedFilterValues;
}) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<SelectedFilterValues>({
    branchId: defaultValues.branchId,
    articleNumber: defaultValues.articleNumber,
    shipmentType: null,
    trackingNumber: '',
    customerId: null,
    includeDispatched: defaultValues.includeDispatched,
    paOrItemCode: '',
  });
  const {
    branchesData: [branches, getBranches],
  } = useMetadata();
  const {
    customersData: [customers, getCustomers],
  } = useMetadata();

  useEffect(() => {
    getBranches();
  }, [getBranches]);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  const setIncludeDispatched = (includeDispatched: boolean) => {
    setFilters((currentFilters) =>
      currentFilters.includeDispatched === includeDispatched
        ? currentFilters
        : { ...currentFilters, includeDispatched },
    );
  };

  const components = useMemo(() => {
    const clearFilters = () => {
      setFilters((currentFilters) => ({
        branchId: defaultValues.branchId,
        articleNumber: defaultValues.articleNumber,
        shipmentType: null,
        trackingNumber: '',
        customerId: null,
        includeDispatched: currentFilters.includeDispatched,
        paOrItemCode: '',
      }));
    };

    return (
      <Flex align="center" flexWrap="wrap" gap={2.5} width="full">
        <EntityDirectorySelectFilter
          buttonProps={{
            h: '42px',
            justifyContent: 'space-between',
            w: { base: 'full', sm: '196px' },
          }}
          options={branches.map((branch) => ({
            label: branch.name,
            value: branch.id,
          }))}
          placeholder={t('filter.allBranches')}
          value={filters.branchId}
          onChange={(branchId) =>
            setFilters((currentFilters) => ({
              ...currentFilters,
              branchId,
            }))
          }
        />
        <EntityDirectorySelectFilter
          buttonProps={{
            h: '42px',
            justifyContent: 'space-between',
            w: { base: 'full', sm: '184px' },
          }}
          options={shipmentTypes}
          placeholder={t('shipment.allTypes')}
          value={filters.shipmentType}
          onChange={(shipmentType) =>
            setFilters((currentFilters) => ({
              ...currentFilters,
              shipmentType,
            }))
          }
        />
        <EntityDirectorySelectFilter
          buttonProps={{
            h: '42px',
            justifyContent: 'space-between',
            w: { base: 'full', sm: '196px' },
          }}
          options={customers.map((customer) => ({
            label: customer.name,
            value: customer.id,
          }))}
          placeholder={t('filter.allBrands')}
          search={{
            maxResults: 50,
            minimumLength: 2,
            noResultsMessage: t('shipment.noBrandsFound'),
            placeholder: t('shipment.searchBrands'),
            prompt: t('shipment.searchBrandsPrompt'),
          }}
          value={filters.customerId}
          onChange={(customerId) =>
            setFilters((currentFilters) => ({
              ...currentFilters,
              customerId,
            }))
          }
        />
        <Flex flex="1 1 220px" maxW={{ base: 'full', xl: '280px' }}>
          <SearchFilterInput
            height="42px"
            placeholder={t('shipment.searchTracking')}
            style="entityDirectory"
            value={filters.trackingNumber}
            onChange={(trackingNumber) =>
              setFilters((currentFilters) => ({
                ...currentFilters,
                trackingNumber,
              }))
            }
          />
        </Flex>
        <Flex flex="1 1 220px" maxW={{ base: 'full', xl: '280px' }}>
          <SearchFilterInput
            height="42px"
            placeholder={t('shipment.searchPaOrItem')}
            style="entityDirectory"
            value={filters.paOrItemCode}
            onChange={(paOrItemCode) =>
              setFilters((currentFilters) => ({
                ...currentFilters,
                paOrItemCode,
              }))
            }
          />
        </Flex>
        <Button
          height="42px"
          variant="entityDirectorySecondary"
          onClick={clearFilters}
        >
          {t('general.clear')}
        </Button>
      </Flex>
    );
  }, [
    branches,
    customers,
    defaultValues.articleNumber,
    defaultValues.branchId,
    filters.branchId,
    filters.customerId,
    filters.paOrItemCode,
    filters.shipmentType,
    filters.trackingNumber,
    t,
  ]);

  return { filters, components, setIncludeDispatched };
}
